import { Bin } from '../../../../types/bin';
import { Container } from '../../../../types/container';
import { CustomField } from '../../../../types/custom_field';
import { Lot } from '../../../../types/lot';
import { Order } from '../../../../types/order';
import { ProductMasterData } from '../../../../types/product_master_data/productMasterData';
import { ProductTransaction } from '../../../../types/productTransaction';
import { StockLocation } from '../../../../types/stock_location/stockLocation';
import { PdfBinDataResolver } from './entities/PdfBin';
import { PdfContainerDataResolver } from './entities/PdfContainer';
import { PdfFileData, PdfFileDataResolver } from './entities/PdfFileDataResolver';
import { PdfLotDataResolver } from './entities/PdfLot';
import { PdfOrderDataResolver } from './entities/PdfOrder';
import { PdfProductMasterDataDataResolver } from './entities/PdfProductMasterData';
import { PdfProductTransactionDataResolver } from './entities/PdfProductTransaction';
import { PdfStockLocationDataResolver } from './entities/PdfStockLocation';
import { PdfDataKey, PdfFieldEntity, PdfSubkey } from './PdfDataKey';
import { PdfEntityDataResolver } from './PdfEntityDataResolver';

export namespace PdfFieldResolving {
  export interface Data {
    stockLocations: Map<string, StockLocation>;
    orders: Map<string, Order>;
    bins: Map<string, Bin>;
    containers: Map<string, Container>;
    lots: Map<string, Lot>;
    productMasterData: Map<string, ProductMasterData>;
    productTransactions: Map<string, ProductTransaction>;
    customFields: Map<string, CustomField>;
    pdf: PdfFileData;
  }

  class MissingSubkeyError extends Error {
    constructor() {
      super('Missing subkey for entity id resolver');
    }
  }

  class UnexpectedSubkeyError extends Error {
    constructor() {
      super('Unexpected subkey for entity id resolver');
    }
  }

  class UnsupportedEntityTypeError extends Error {
    constructor() {
      super('Unsupported entity type for entity id resolver');
    }
  }

  type EntityIdResolver<SE> = (entity: SE, key: PdfDataKey<any>) => string | undefined;

  const orderEntityIdResolver: EntityIdResolver<Order> = (order: Order, key: PdfDataKey<any>): string | undefined => {
    switch (key.entityType) {
      case PdfFieldEntity.order:
        return order.id;
      case PdfFieldEntity.stockLocation:
        if (!key.subkey) throw new MissingSubkeyError();
        return key.subkey === PdfSubkey.source ? order.fromStockLocationId : order.toStockLocationId;

      default:
        throw new UnsupportedEntityTypeError();
    }
  };

  const productTransactionEntityIdResolver: EntityIdResolver<ProductTransaction> = (
    pt: ProductTransaction,
    key: PdfDataKey<any>,
  ): string | undefined => {
    switch (key.entityType) {
      case PdfFieldEntity.productTransaction:
        return pt.id;
      case PdfFieldEntity.productMasterData:
        return pt.product.pmdId;
      case PdfFieldEntity.bin:
        if (!key.subkey) throw new MissingSubkeyError();
        return key.subkey === PdfSubkey.source ? pt.product.fromBinId : pt.product.toBinId;
      case PdfFieldEntity.container:
        if (!key.subkey) throw new MissingSubkeyError();
        return key.subkey === PdfSubkey.source ? pt.product.fromContainerId : pt.product.toContainerId;
      case PdfFieldEntity.lot:
        if (key.subkey) throw new UnexpectedSubkeyError();
        return pt.product.lotId;
      default:
        throw new UnsupportedEntityTypeError();
    }
  };

  function getEntityId(entity: any, entityType: PdfFieldEntity, key: PdfDataKey<any>): string | undefined {
    if (key.entityType === PdfFieldEntity.pdf) return 'custom';

    switch (entityType) {
      case PdfFieldEntity.order:
        return orderEntityIdResolver(entity, key);
      case PdfFieldEntity.productTransaction:
        return productTransactionEntityIdResolver(entity, key);

      default:
        throw new UnsupportedEntityTypeError();
    }
  }

  function getEntity(entityId: string, key: PdfDataKey<any>, data: Data): any {
    switch (key.entityType) {
      case PdfFieldEntity.bin:
        return data.bins.get(entityId);
      case PdfFieldEntity.container:
        return data.containers.get(entityId);
      case PdfFieldEntity.order:
        return data.orders.get(entityId);
      case PdfFieldEntity.productMasterData:
        return data.productMasterData.get(entityId);
      case PdfFieldEntity.productTransaction:
        return data.productTransactions.get(entityId);
      case PdfFieldEntity.stockLocation:
        return data.stockLocations.get(entityId);
      case PdfFieldEntity.lot:
        return data.lots.get(entityId);
      case PdfFieldEntity.pdf:
        return data.pdf;
    }
  }

  function getResolver(entityType: PdfFieldEntity, data: Data): PdfEntityDataResolver<any, any> {
    switch (entityType) {
      case PdfFieldEntity.bin:
        return new PdfBinDataResolver();
      case PdfFieldEntity.container:
        return new PdfContainerDataResolver();
      case PdfFieldEntity.order:
        return new PdfOrderDataResolver(data.productTransactions);
      case PdfFieldEntity.productMasterData:
        return new PdfProductMasterDataDataResolver();
      case PdfFieldEntity.productTransaction:
        return new PdfProductTransactionDataResolver(data.productMasterData);
      case PdfFieldEntity.stockLocation:
        return new PdfStockLocationDataResolver();
      case PdfFieldEntity.lot:
        return new PdfLotDataResolver();
      case PdfFieldEntity.pdf:
        return new PdfFileDataResolver();
    }
  }

  export function getValue(entity: any, entityType: PdfFieldEntity, key: PdfDataKey<any>, data: Data): string {
    const entityId = getEntityId(entity, entityType, key);
    if (!entityId) return '';
    const resolver = getResolver(key.entityType, data);
    const target = getEntity(entityId, key, data);
    if (!target) return 'Unknown Entity';
    return resolver.getValueByDataKey(key, target);
  }

  export function getLabel(key: PdfDataKey<any>, data: Data): string {
    const resolver = getResolver(key.entityType, data);
    return resolver.getLabelByDataKey(data.customFields, key);
  }
}
